
import { useCallback } from "react";
import { Movie } from "@/types/movie";

interface UsePlanToWatchHandlerProps {
  movie: Movie;
  tmdbId: number;
  isValidMovie: boolean;
  favoriteVisualState: any;
  planToWatchVisualState: any;
  isImmediate: boolean;
  context: string;
  currentTab?: string;
  onTogglePlanToWatch?: (e: React.MouseEvent) => void | Promise<void>;
  commitTogglePlanToWatch: (movie: Movie) => Promise<void>;
  togglePlanToWatch: (tmdbId: number, currentTab?: string, movie?: Movie) => void | Promise<void>;
  setPlanToWatchDeselected: (value: boolean) => void;
}

/**
 * Hook to handle plan to watch button clicks
 */
export const usePlanToWatchHandler = ({
  movie,
  tmdbId,
  isValidMovie,
  favoriteVisualState,
  planToWatchVisualState,
  isImmediate,
  context,
  currentTab,
  onTogglePlanToWatch,
  commitTogglePlanToWatch,
  togglePlanToWatch,
  setPlanToWatchDeselected
}: UsePlanToWatchHandlerProps) => {
  
  const handlePlanToWatchClick = useCallback(async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isValidMovie) {
      console.error('🔘 [BUTTONS] Cannot toggle plan to watch: Invalid tmdbId:', {
        tmdbId,
        title: movie.title || 'Unknown'
      });
      return;
    }
    
    try {
      console.log(`🔍 [BUTTONS-WATCH] Watchlist button clicked for: ${movie.title}`, {
        tmdbId,
        isImmediate,
        context,
        currentMovieState: {
          favorite: favoriteVisualState.currentValue,
          planToWatch: planToWatchVisualState.currentValue
        },
        expectedChange: 'planToWatch only'
      });
      
      // REMOVED: setPlanToWatchDeselected(planToWatchVisualState.currentValue);
      // This was causing the button flickering issue
      
      if (onTogglePlanToWatch) {
        console.log(`🔍 [BUTTONS-WATCH] Using custom watchlist handler for: ${movie.title}`);
        await Promise.resolve(onTogglePlanToWatch(e));
      } else if (isImmediate) {
        console.log(`🔍 [BUTTONS-WATCH] Immediate commit watchlist toggle for: ${movie.title} (${context})`);
        await commitTogglePlanToWatch(movie);
      } else {
        console.log(`🔍 [BUTTONS-WATCH] Collection item watchlist toggle for: ${movie.title} (${context}) - should NOT affect favorite`);
        await Promise.resolve(togglePlanToWatch(tmdbId, currentTab, movie));
      }
      
      console.log(`✅ [BUTTONS-WATCH] Watchlist toggle completed for: ${movie.title}`);
    } catch (error) {
      console.error('❌ [BUTTONS-WATCH] Error in plan to watch click handler:', error);
      setPlanToWatchDeselected(false);
    }
  }, [movie, onTogglePlanToWatch, isImmediate, commitTogglePlanToWatch, togglePlanToWatch, currentTab, favoriteVisualState.currentValue, planToWatchVisualState.currentValue, tmdbId, isValidMovie, context, setPlanToWatchDeselected]);

  return { handlePlanToWatchClick };
};
